import _extends from "@babel/runtime/helpers/esm/extends";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["children", "chipElevation", "defaultValue", "disabled", "error", "fluid", "icon", "labelVoiceOver", "onChange", "required", "size", "value"];
import PropTypes from 'prop-types';
import React, { forwardRef, useCallback, useRef, useState } from 'react';
import { identifier } from '../../util/identifier';
import { Select } from '../Select/Select';
import { VisuallyHidden } from '../VisuallyHidden/VisuallyHidden';
import { StyledFilterChipWithMenu } from './FilterChipWithMenu.styles';

/**
 * Filter chips with menus behave similar to select fields. They're used mainly
 * for filtering and sorting content (ex: search filters).
 *
 * @extends Select
 * @related ['AssistChip', 'InputChip']
 * @since 8.97.0
 */
export var FilterChipWithMenu = /*#__PURE__*/forwardRef(function (props, forwardedRef) {
  var children = props.children,
    chipElevation = props.chipElevation,
    defaultValue = props.defaultValue,
    disabled = props.disabled,
    error = props.error,
    fluid = props.fluid,
    icon = props.icon,
    labelVoiceOver = props.labelVoiceOver,
    onChange = props.onChange,
    required = props.required,
    size = props.size,
    value = props.value,
    rest = _objectWithoutProperties(props, _excluded);
  var _useState = useState(!!props.value || !!props.defaultValue),
    _useState2 = _slicedToArray(_useState, 2),
    isToggledOn = _useState2[0],
    setIsToggledOn = _useState2[1];

  // Generate an id to link label to the select
  var selectName = useRef(identifier()).current;
  var changeCallback = useCallback(function (e) {
    setIsToggledOn(!!e.target.value);
    if (onChange) {
      onChange(e);
    }
  }, [onChange]);
  return /*#__PURE__*/React.createElement(StyledFilterChipWithMenu, _extends({
    chipElevation: chipElevation,
    disabled: disabled,
    fluid: false,
    icon: icon,
    isToggledOn: isToggledOn,
    ref: forwardedRef,
    htmlFor: selectName
  }, rest), /*#__PURE__*/React.createElement(VisuallyHidden, null, labelVoiceOver), icon, /*#__PURE__*/React.createElement(Select, {
    id: selectName,
    defaultValue: defaultValue,
    disabled: disabled,
    onChange: changeCallback,
    value: value
  }, children));
});
FilterChipWithMenu.displayName = 'FilterChipWithMenu';
FilterChipWithMenu.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * Accepts `<option>` and `<optgroup>` nodes.
   *
   * Note that the first `option` element must have a value of empty string
   * (ex: `<option value="">`) if it's acting as the label for the chip. This is how
   * the chip knows whether to display in its default state (no value selected) vs its
   * "selected" state (value selected).
   */
  children: PropTypes.node.isRequired,
  /**
   * When true, adds a drop shadow. Useful for making the chip easier to see on
   * certain backgrounds (ex: on top of a photo or map)
   */
  chipElevation: PropTypes.bool,
  /**
   * The value of the default, selected option when using this as an
   * [uncontrolled component](https://reactjs.org/docs/uncontrolled-components.html).
   */
  defaultValue: PropTypes.string,
  /**
   * Sets the chip as disabled.
   */
  disabled: PropTypes.bool,
  /**
   * Adds a leading icon. Takes an `Icon` component but ignores its `size` prop
   * (i.e. the icon size is fixed). Does not support `DetailedIcon`.
   */
  icon: PropTypes.node,
  /**
   * The label that will be announced to screen readers when you focus on the
   * chip or change your selection. Rendered as visually hidden text.
   *
   * Defaults to announcing "filter". The text should reflect the type of action this
   * chip provides. Do not include the value of the selected menu option since
   * assistive technologies will usually announce this anyway.
   */
  labelVoiceOver: PropTypes.string,
  /**
   * A callback that is triggered when a different option is selected. Passes the event object.
   */
  onChange: PropTypes.func,
  /**
   * The value of the selected option when using this as a
   * [controlled component](https://reactjs.org/docs/forms.html#controlled-components).
   */
  value: PropTypes.string
} : {};
FilterChipWithMenu.defaultProps = {
  chipElevation: false,
  disabled: false,
  icon: null,
  labelVoiceOver: 'Filter'
};